'use client';

import usePersistentContext from 'hooks/usePersistenceState';
import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect } from 'react';

export const MODAL_OPEN_TRANSLATION_REQUEST_PERSISTED_STATES =
  'MODAL_OPEN_TRANSLATION_REQUEST_PERSISTED_STATES';
export const MODAL_TRANSLATION_REQUEST_PERSISTED_STATES =
  'MODAL_TRANSLATION_REQUEST_PERSISTED_STATES';
export const VERSION_TRANSLATION_REQUEST_PERSISTED_STATES =
  'VERSION_TRANSLATION_REQUEST_PERSISTED_STATES';
export const TR_VERSION_TRANSLATION_REQUEST_PERSISTED_STATES =
  'TR_VERSION_TRANSLATION_REQUEST_PERSISTED_STATES';

type TModalTranslationRequestScreens = {
  order?: boolean;
  accept?: boolean;
  files?: boolean;
  quote?: boolean;
  tasks?: boolean;
};

type TModalTranslationRequestContext = {
  modal?: TModalTranslationRequestScreens;
  open?: boolean;
  version?: string;
  trVersions?: string[];
  setTrVersions: (newValue: string[]) => void;
  setVersion: (newValue: string) => void;
  onClose: () => void;
  openModal: (screen: keyof TModalTranslationRequestScreens) => void;
};

const initialState = {
  open: false,
  trVersions: [],
  version: '',
  modal: {
    order: false,
    accept: false,
    files: false,
    quote: false,
    tasks: false
  },
  orderOpen: false,
  onClose: () => {},
  setTrVersions: () => {},
  setVersions: () => {},
  setVersion: () => {},
  openModal: () => {}
};

const ModalTranslationRequestContext = createContext<TModalTranslationRequestContext>(initialState);

const useModalTranslationRequestContext = (): TModalTranslationRequestContext =>
  useContext(ModalTranslationRequestContext);

const ModalTranslationRequestProvider: FC<PropsWithChildren<unknown>> = ({
  children
}): JSX.Element => {
  const [open, setOpen] = usePersistentContext<boolean>(
    MODAL_OPEN_TRANSLATION_REQUEST_PERSISTED_STATES
  );
  const [modal, setModal] = usePersistentContext<TModalTranslationRequestScreens>(
    MODAL_TRANSLATION_REQUEST_PERSISTED_STATES
  );

  const [version, setVersion] = usePersistentContext<string>(
    VERSION_TRANSLATION_REQUEST_PERSISTED_STATES
  );
  const [trVersions, setTrVersions] = usePersistentContext<string[]>(
    TR_VERSION_TRANSLATION_REQUEST_PERSISTED_STATES
  );

  const onClose = () => {
    setOpen(false);
    setVersion(initialState.version);
    setTrVersions(initialState.trVersions);
    setTimeout(() => {
      setModal(initialState.modal);
    }, 100);
  };

  const openModal = useCallback(
    (screen: keyof TModalTranslationRequestScreens) => {
      setOpen(true);
      setModal({ ...modal, [screen]: true });
    },
    [modal, setModal, setOpen]
  );

  useEffect(() => {
    if (!open) setOpen(initialState.open);
    if (!modal) setModal(initialState.modal);
    if (!version) setVersion(initialState.version);
    if (!trVersions) setTrVersions(initialState.trVersions);
  }, [modal, open, setModal, setOpen, setTrVersions, setVersion, trVersions, version]);

  return (
    <ModalTranslationRequestContext.Provider
      value={{
        modal,
        open,
        version,
        trVersions,
        setTrVersions,
        setVersion,
        onClose,
        openModal
      }}
    >
      {children}
    </ModalTranslationRequestContext.Provider>
  );
};

export { ModalTranslationRequestProvider, useModalTranslationRequestContext };
